'use client'

import { useState, useMemo, useCallback } from 'react'

interface CurrencyInfo {
  code: string
  name: string
  symbol: string
  price: number
  high24h: number
  low24h: number
}

interface Props {
  coinName: string
  coinSymbol: string
  coinImage: string
  currencies: CurrencyInfo[]
  dailyChanges: number[] // last 7 days % changes
  change7d: number
}

export function CryptoConverter({ coinName, coinSymbol, coinImage, currencies, dailyChanges, change7d }: Props) {
  const [cryptoAmount, setCryptoAmount] = useState('1')
  const [selectedCurrency, setSelectedCurrency] = useState('usd')
  const [direction, setDirection] = useState<'crypto-to-fiat' | 'fiat-to-crypto'>('crypto-to-fiat')
  const [fiatAmount, setFiatAmount] = useState('')

  const activeCurrency = useMemo(
    () => currencies.find((c) => c.code === selectedCurrency) ?? currencies[0],
    [currencies, selectedCurrency]
  )

  const computedFiat = useMemo(() => {
    const amt = parseFloat(cryptoAmount)
    if (isNaN(amt) || amt <= 0) return 0
    return amt * activeCurrency.price
  }, [cryptoAmount, activeCurrency])

  const computedCrypto = useMemo(() => {
    const amt = parseFloat(fiatAmount)
    if (isNaN(amt) || amt <= 0 || activeCurrency.price <= 0) return 0
    return amt / activeCurrency.price
  }, [fiatAmount, activeCurrency])

  const handleCryptoChange = useCallback((val: string) => {
    setCryptoAmount(val)
    setDirection('crypto-to-fiat')
  }, [])

  const handleFiatChange = useCallback((val: string) => {
    setFiatAmount(val)
    setDirection('fiat-to-crypto')
  }, [])

  const handleSwap = useCallback(() => {
    if (direction === 'crypto-to-fiat') {
      setFiatAmount(computedFiat.toString())
      setDirection('fiat-to-crypto')
    } else {
      setCryptoAmount(computedCrypto.toString())
      setDirection('crypto-to-fiat')
    }
  }, [direction, computedFiat, computedCrypto])

  // Format display
  const fmtDisplay = (n: number) => {
    if (n === 0) return ''
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`
    if (n >= 1000) return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    if (n >= 1) return n.toFixed(2)
    if (n >= 0.01) return n.toFixed(4)
    return n.toPrecision(4)
  }

  const fmtCrypto = (n: number) => {
    if (n === 0) return ''
    if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
    if (n >= 1) return n.toFixed(6)
    return n.toPrecision(6)
  }

  // Rate bar position (24h range)
  const ratePct = useMemo(() => {
    const range = activeCurrency.high24h - activeCurrency.low24h
    if (range <= 0) return 50
    return Math.max(0, Math.min(100, ((activeCurrency.price - activeCurrency.low24h) / range) * 100))
  }, [activeCurrency])

  const isPositive7d = change7d >= 0
  const presets = useMemo(() => {
    const usdP = currencies.find((c) => c.code === 'usd')?.price ?? 0
    if (usdP >= 10000) return [0.01, 0.1, 0.5, 1]
    if (usdP >= 1000) return [0.1, 0.5, 1, 5]
    if (usdP >= 100) return [1, 5, 10, 50]
    if (usdP >= 10) return [1, 10, 50, 100]
    if (usdP >= 1) return [10, 50, 100, 500]
    if (usdP >= 0.01) return [100, 1000, 5000, 10000]
    return [1000, 10000, 100000, 1000000]
  }, [currencies])
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 lg:items-start">
      {/* ═══ LEFT: CONVERTER WIDGET + PILLS ═══ */}
      <div>
        <div className="border border-zinc-800">
        {/* Crypto input */}
        <div className="px-4 sm:px-5 py-4 border-b border-zinc-800">
          <div className="mb-2">
            <span className="text-zinc-400 text-[10px] uppercase tracking-wider">
              {direction === 'crypto-to-fiat' ? 'You have' : 'You get'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <img src={coinImage} alt={coinName} width={28} height={28} className="rounded-full flex-shrink-0" />
            <input
              type="text"
              inputMode="decimal"
              value={direction === 'crypto-to-fiat' ? cryptoAmount : fmtCrypto(computedCrypto)}
              onChange={(e) => handleCryptoChange(e.target.value)}
              onFocus={() => setDirection('crypto-to-fiat')}
              className="flex-1 bg-transparent text-white text-xl sm:text-2xl font-semibold tabular-nums outline-none min-w-0"
              placeholder="0"
            />
            <span className="text-zinc-400 text-sm font-medium flex-shrink-0">{coinSymbol}</span>
          </div>
        </div>

        {/* Swap button */}
        <div className="relative h-0">
          <button
            onClick={handleSwap}
            className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-zinc-900 border border-zinc-700 flex items-center justify-center hover:bg-zinc-800 hover:border-zinc-600 transition-colors"
            aria-label="Swap direction"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-400">
              <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </button>
        </div>

        {/* Fiat input */}
        <div className="px-4 sm:px-5 py-4 border-b border-zinc-800">
          <div className="mb-2">
            <span className="text-zinc-400 text-[10px] uppercase tracking-wider">
              {direction === 'crypto-to-fiat' ? 'You get' : 'You have'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-zinc-300 text-lg w-7 text-center flex-shrink-0">{activeCurrency.symbol}</span>
            <input
              type="text"
              inputMode="decimal"
              value={direction === 'fiat-to-crypto' ? fiatAmount : fmtDisplay(computedFiat)}
              onChange={(e) => handleFiatChange(e.target.value)}
              onFocus={() => setDirection('fiat-to-crypto')}
              className="flex-1 bg-transparent text-white text-xl sm:text-2xl font-semibold tabular-nums outline-none min-w-0"
              placeholder="0"
            />
             <select
              aria-label="Select currency"
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 text-zinc-300 text-[11px] sm:text-sm px-1.5 sm:px-2 py-1.5 outline-none hover:border-zinc-600 transition-colors flex-shrink-0 cursor-pointer max-w-[120px] sm:max-w-none"
              >
              {currencies.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code.toUpperCase()} — {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 7d heatmap strip */}
        {dailyChanges.length > 0 && (
          <div className="flex items-center gap-3 px-4 sm:px-5 py-2.5 border-b border-zinc-800/50 bg-zinc-950/30">
            <span className="text-zinc-400 text-[10px] flex-shrink-0 whitespace-nowrap">7d</span>
            <div className="flex-1 flex gap-[2px] h-[14px]">
              {dailyChanges.slice(-7).map((change, i) => {
                const abs = Math.abs(change)
                const opacity = abs >= 5 ? 0.5 : abs >= 3 ? 0.35 : abs >= 1 ? 0.25 : 0.12
                const color = change >= 0
                  ? `rgba(34,197,94,${opacity})`
                  : `rgba(239,68,68,${opacity})`
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-[1px]"
                    style={{ background: color }}
                  />
                )
              })}
            </div>
            <span className={`text-[11px] font-semibold tabular-nums flex-shrink-0 ${isPositive7d ? 'text-green-500' : 'text-red-500'}`}>
              {isPositive7d ? '+' : ''}{change7d.toFixed(2)}%
            </span>
          </div>
        )}

        {/* 24h rate context bar */}
        <div className="px-4 sm:px-5 py-3 bg-zinc-950/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400 text-[10px] uppercase tracking-wider">24h rate position</span>
            <span className={`text-[11px] font-semibold tabular-nums ${ratePct >= 50 ? 'text-green-500' : 'text-red-500'}`}>
              {ratePct.toFixed(0)}% of 24h high
            </span>
          </div>
          <div className="relative h-1 rounded-full" style={{ background: 'linear-gradient(to right, #ef4444, #eab308 50%, #22c55e)' }}>
            <div
              className="absolute top-[-7px]"
              style={{ left: `${ratePct}%`, transform: 'translateX(-50%)' }}
            >
              <svg width="8" height="5" viewBox="0 0 8 5" fill="white">
                <path d="M4 5L0 0H8L4 5Z" />
              </svg>
            </div>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-zinc-400 text-[9px] tabular-nums">
              {activeCurrency.symbol}{fmtDisplay(activeCurrency.low24h)}
            </span>
            <span className="text-zinc-400 text-[9px] tabular-nums">
              {activeCurrency.symbol}{fmtDisplay(activeCurrency.high24h)}
            </span>
          </div>
        </div>
      </div>

      {/* Preset pills */}
      <div className="flex flex-wrap gap-2 mt-3">
        {presets.map((amt) => (
          <button
            key={amt}
            onClick={() => { setCryptoAmount(String(amt)); setDirection('crypto-to-fiat') }}
            className={`px-3 py-1.5 text-xs tabular-nums border transition-colors ${
              cryptoAmount === String(amt) && direction === 'crypto-to-fiat'
                ? 'border-zinc-400 text-white'
                : 'border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
            }`}
          >
            {amt} {coinSymbol}
          </button>
        ))}
      </div>
      </div>

      {/* ═══ RIGHT: MULTI-CURRENCY GRID (grouped by region) ═══ */}
      <div>
        <div className="border border-zinc-800">
          <div className="px-3 py-2 border-b border-zinc-800">
            <span className="text-zinc-400 text-xs uppercase tracking-widest" style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}>
              1 {coinSymbol} in other currencies
            </span>
          </div>
          {[
          { region: 'Americas', codes: ['usd', 'cad', 'brl', 'mxn'] },
          { region: 'Europe', codes: ['eur', 'gbp', 'chf', 'try', 'rub', 'pln'] },
          { region: 'Asia-Pacific', codes: ['jpy', 'cny', 'krw', 'inr', 'aud', 'hkd'] },
          ].map((group, gi) => {
            const groupCurrencies = group.codes
              .map((code) => currencies.find((c) => c.code === code))
              .filter(Boolean) as typeof currencies
            if (groupCurrencies.length === 0) return null

            return (
              <div key={group.region}>
                <div className={`px-3 py-1.5 ${gi > 0 ? 'border-t border-zinc-800' : ''}`}>
                  <span className="text-zinc-400 text-[9px] uppercase tracking-widest">{group.region}</span>
                </div>
                <div className="grid grid-cols-2 gap-px bg-zinc-800/50">
                  {groupCurrencies.map((c) => (
                    <div
                      key={c.code}
                      className={`flex items-center justify-between px-3 py-2.5 bg-black cursor-pointer transition-colors hover:bg-zinc-900/40 ${
                        c.code === selectedCurrency ? 'bg-zinc-900/30' : ''
                      }`}
                      onClick={() => { setSelectedCurrency(c.code); setDirection('crypto-to-fiat') }}
                    >
                      <span className="text-zinc-400 text-[11px] uppercase font-medium">{c.code}</span>
                      <span className={`text-[12px] tabular-nums font-semibold ${
                        c.code === selectedCurrency ? 'text-green-500' : 'text-white'
                      }`}>
                        {c.symbol}{fmtDisplay(c.price)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}